/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2017, 2023
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, useMemo, useEffect } from 'react';
import { Button, TextInput } from '@carbon/react';
import Invoker from '../utils/invoker';
import { getOauthErrorMessage } from '../utils/error-message-handler';
import { View, ViewOff } from '@carbon/react/icons';
import { useTranslation } from 'react-i18next';
import { getMixedContentMessage } from '../utils/error-message-handler';

const TestToolAccessToken = ({ config, onChange }) => {
  const [authToken, setAuthToken] = useState({ access_token: '' });
  const [authError, setAuthError] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const invoker = useMemo(() => new Invoker(), []);
  const { t } = useTranslation();

  useEffect(() => {
    if (!authToken.access_token) {
      setAuthToken({ access_token: '' });
    }
  }, [authToken.access_token]);

  const getToken = async (event) => {
    let authToken = {
      access_token: ''
    };

    // mixed content check - i.e. calling http from https - not allowed.
    if (window.location.protocol === 'https:' && config.flags.selectedTokenUrl.lastIndexOf(window.location.protocol) !== 0) {
      setAuthToken(authToken);
      setAuthError(getMixedContentMessage(t));
      return;
    }

    if (config.flags.oauthFlow === 'implicit') {
      // authorize calls call window.open, we can't get a handle on the response so just call it.
      invoker.authorize(config.flags, config.identification.clientID, config.scopes, config.identification.redirectUri);
    } else {
      try {
        let response = await invoker.getToken(
          config.flags,
          config.authToken,
          config.identification.clientID,
          config.identification.clientSecret,
          config.scopes,
          config.identification.redirectUri,
          config.userCredentials.username,
          config.userCredentials.password
        );

        setAuthError({});
        Object.keys(response).forEach((key) => {
          authToken[key] = response[key];
        });
      } catch (err) {
        setAuthError(await getOauthErrorMessage(err, t));
      } finally {
        setAuthToken(authToken);
        if (onChange && typeof onChange === 'function') {
          onChange(authToken);
        }
      }
    }
  };

  const updateToken = (event) => {
    let newToken = {};
    newToken['access_token'] = event.target.value;
    setAuthToken(newToken);

    if (onChange && typeof onChange === 'function') {
      onChange(newToken);
    }
  };

  let helpText = '';
  if (config.removeFields) {
    helpText =
      config.flags.oauthFlow === 'accessCode' || config.flags.oauthFlow === 'authorizationCode' ? t('explorer_help_access_code') : t('explorer_help_implicit');
  }

  return (
    <>
      <div className="getToken">
        {!config.removeFields && (
          <Button className="getTokenButton" onClick={getToken}>
            {t('explorer_get_token')}
          </Button>
        )}
        <TextInput
          className="tokenField"
          labelText={t('explorer_access_token')}
          type={showPassword ? 'text' : 'password'}
          id="tokenField"
          value={authToken['access_token']}
          onChange={updateToken}
          helperText={helpText}
        />
        <Button
          hasIconOnly
          className="togglePassword"
          kind="ghost"
          iconDescription={showPassword ? 'Hide access token' : 'Show access token'}
          renderIcon={showPassword ? ViewOff : View}
          onClick={() => {
            setShowPassword(!showPassword);
          }}
        />
      </div>
      {authError && authError.highlightedText && (
        <div className="authError">
          <pre>
            <code className="boundedSection hljs oauthError" dangerouslySetInnerHTML={authError.highlightedText} />
          </pre>
        </div>
      )}
    </>
  );
};
export default TestToolAccessToken;
